import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { Text, MessageBox } from '@reservamos/elements';
import useWhitelabelEnvs from 'hooks/whitelabel/useWhitelabelEnvs';
import useIsMobile from 'hooks/useIsMobile';
import useYapeValidation from 'hooks/useYapeValidation';
import YapeInfoModal from './YapeInfoModal';
import YapeInfoButton from './YapeInfoButton';
import YapeFormField from './YapeFormField';
import YapeInfoSection from './YapeInfoSection';

/**
 * YapeForm component.
 * Renders a form for Yape payment method without its own submit button.
 * Exposes form state and validation to parent component.
 * @param {Object} props - Component props
 * @param {Function} props.onFormDataChange - Callback to update parent with form data
 * @param {Function} props.onValidationChange - Callback to update parent with validation state
 * @param {boolean} props.isSubmitting - Whether the form is currently submitting
 * @param {Object} props.initialData - Initial form data (optional)
 */
const YapeForm = ({ onFormDataChange, onValidationChange, isSubmitting, initialData = {} }) => {
  const { t } = useTranslation('payment');
  const [otp, setOtp] = useState(initialData.otp || '');
  const [phoneNumber, setPhoneNumber] = useState(initialData.phoneNumber || '');
  const [showInfoModal, setShowInfoModal] = useState(false);
  const isMobile = useIsMobile();

  const { mercadoPago } = useWhitelabelEnvs();
  const { enabled: mercadoPagoIsEnabled } = mercadoPago || { enabled: false };

  const { errors, fieldInteractions, validateField, markFieldAsInteracted, hasError } =
    useYapeValidation(initialData);

  useEffect(() => {
    const formData = {
      otp: otp.replace(/\D/g, ''),
      phoneNumber: phoneNumber.replace(/\D/g, ''),
    };
    onFormDataChange(formData);
  }, [otp, phoneNumber, onFormDataChange]);

  useEffect(() => {
    const hasAnyInteraction = fieldInteractions.phoneNumber || fieldInteractions.otp;
    const valid = hasAnyInteraction ? !Object.keys(errors).some((key) => errors[key]) : false;
    onValidationChange(valid);
  }, [errors, fieldInteractions, onValidationChange]);

  /**
   * Handles phone number input change and formatting.
   * @param {Event} event - The input change event
   */
  const handlePhoneNumberChange = (event) => {
    const { value } = event.target;
    const numbers = value.replace(/\D/g, '');
    if (numbers.length <= 9) {
      const formatted = numbers.replace(/(\d{3})(\d{3})(\d{3})/, '$1-$2-$3');
      setPhoneNumber(formatted);

      if (numbers.length === 9) {
        validateField('phoneNumber', formatted);
      }
    }
  };

  /**
   * Handles OTP input change and validation.
   * @param {Event} event - The input change event
   */
  const handleOtpChange = (event) => {
    const { value } = event.target;
    const numbers = value.replace(/\D/g, '');
    if (numbers.length <= 6) {
      setOtp(numbers);

      if (numbers.length === 6) {
        validateField('otp', numbers);
      }
    }
  };

  /**
   * Handles phone number input blur event for validation.
   */
  const handlePhoneNumberBlur = () => {
    markFieldAsInteracted('phoneNumber');
    validateField('phoneNumber', phoneNumber);
  };

  /**
   * Handles OTP input blur event for validation.
   */
  const handleOtpBlur = () => {
    markFieldAsInteracted('otp');
    validateField('otp', otp);
  };

  /**
   * Opens the info modal.
   */
  const handleOpenInfoModal = () => {
    setShowInfoModal(true);
  };

  /**
   * Closes the info modal.
   */
  const handleCloseInfoModal = () => {
    setShowInfoModal(false);
  };

  if (!mercadoPagoIsEnabled) {
    return (
      <MessageBox title={t('error.payment_method_unavailable')} borderColor="error">
        <Text size="S">{t('error.mercado_pago_disabled')}</Text>
      </MessageBox>
    );
  }

  return (
    <div
      className={`bg-white rounded-xl shadow-[0_10px_25px_rgba(0,0,0,0.1)] border border-gray-100 overflow-hidden flex min-h-[300px] my-5 ${
        isMobile ? 'flex-col min-h-auto my-4' : ''
      }`}
    >
      {isMobile ? (
        <>
          {/* Mobile: Purple section on top */}
          <YapeInfoSection t={t} isMobile />

          {/* Mobile: Form section below */}
          <div className="flex-none p-5 bg-white rounded-b-xl">
            <YapeFormField
              id="payerPhone"
              name="payerPhone"
              type="tel"
              label={t('yape.phone_number')}
              value={phoneNumber}
              onChange={handlePhoneNumberChange}
              onBlur={handlePhoneNumberBlur}
              placeholder="999-999-999"
              maxLength="11"
              hasError={hasError('phoneNumber')}
              errorMessage={errors.phoneNumber}
              disabled={isSubmitting}
            />

            <YapeFormField
              id="payerOTP"
              name="payerOTP"
              type="text"
              label={t('yape.otp')}
              value={otp}
              onChange={handleOtpChange}
              onBlur={handleOtpBlur}
              placeholder="123456"
              maxLength="6"
              hasError={hasError('otp')}
              errorMessage={errors.otp}
              disabled={isSubmitting}
              instructions={t('yape.otp_instructions')}
            />

            {/* Info button */}
            <YapeInfoButton onClick={handleOpenInfoModal} text={t('yape.info_link')} />
          </div>
        </>
      ) : (
        <>
          {/* Desktop: Side-by-side layout */}
          <YapeInfoSection t={t} isMobile={false} />

          {/* Desktop: Right side with form */}
          <div className="flex-[1.2] bg-white p-4 flex flex-col justify-center items-center">
            <YapeFormField
              id="payerPhone"
              name="payerPhone"
              type="tel"
              label={t('yape.phone_number')}
              value={phoneNumber}
              onChange={handlePhoneNumberChange}
              onBlur={handlePhoneNumberBlur}
              placeholder="999-999-999"
              maxLength="11"
              hasError={hasError('phoneNumber')}
              errorMessage={errors.phoneNumber}
              disabled={isSubmitting}
            />

            <YapeFormField
              id="payerOTP"
              name="payerOTP"
              type="text"
              label={t('yape.otp')}
              value={otp}
              onChange={handleOtpChange}
              onBlur={handleOtpBlur}
              placeholder="123456"
              maxLength="6"
              hasError={hasError('otp')}
              errorMessage={errors.otp}
              disabled={isSubmitting}
              instructions={t('yape.otp_instructions')}
            />

            {/* Info button */}
            <YapeInfoButton onClick={handleOpenInfoModal} text={t('yape.info_link')} />
          </div>
        </>
      )}

      {/* Info Modal */}
      <YapeInfoModal isVisible={showInfoModal} onClose={handleCloseInfoModal} />
    </div>
  );
};

YapeForm.propTypes = {
  onFormDataChange: PropTypes.func.isRequired,
  onValidationChange: PropTypes.func.isRequired,
  isSubmitting: PropTypes.bool,
  initialData: PropTypes.object,
};

YapeForm.defaultProps = {
  isSubmitting: false,
  initialData: {},
};

export default YapeForm;
